/**
 * Offline saving (V19.14).
 *
 * In the shop the signal comes and goes. The things a person does on an open
 * list there - tick, untick, change a quantity, type a price, add an item -
 * are kept on the phone when they can't be sent, and sent in the order they
 * were made once the phone is back online.
 *
 * Rules that keep it safe:
 *  - Every saved change belongs to the account that made it, and is only
 *    ever sent while that same account is signed in on this phone.
 *  - Adds carry an id the phone made (client_id). The server treats a second
 *    copy of the same add as the same item, so a lost answer or a resend can
 *    never make two rows.
 *  - One send at a time (single flight), oldest first. A send that can't get
 *    through stops the run and keeps everything after it, so order is kept.
 *  - A change the server turns down (the list or item was removed meanwhile)
 *    is dropped and counted, so the person is told rather than it retrying
 *    for ever.
 *
 * Everything else in the app still needs a connection, and says so.
 */
import axios from 'axios';
import {
  addToSyncQueue,
  getSyncQueue,
  removeFromSyncQueue,
  put,
  getById,
  clearStore,
  STORES,
} from './offlineStorage';

const KIND = 'xhr';
const EVENT = 'xel-offline-writes';
export const SYNCED_EVENT = 'xel-offline-synced';

let pending = 0;          // this account's waiting changes, kept for sync reads
let flushing = null;      // the one send run in progress, if any

const emit = () => {
  try { window.dispatchEvent(new CustomEvent(EVENT, { detail: { count: pending } })); } catch (e) { /* noop */ }
};

/** No connection at all, as the phone sees it. */
export const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

/**
 * The request never got an answer (no signal, timed out, dropped). An answer
 * of any kind - even an error - means the server was reached, so it is not
 * this.
 */
export const isNetworkFailure = (e) => {
  if (!e || e.response) return false;
  if (axios.isCancel && axios.isCancel(e)) return false;
  return true;
};

/** A random id in the form the server accepts for client_id. */
export const newClientId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (e) { /* fall through */ }
  const b = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
};

const mine = (owner) => (a) => a && a.kind === KIND && a.owner === owner;

// A step (quantity_delta) is sent on its own, so it only merges with another
// step, and a plain change only with another plain change (V19.15).
const mergeable = (a, b) => {
  const stepA = !!(a && a.quantity_delta != null);
  const stepB = !!(b && b.quantity_delta != null);
  if (stepA !== stepB) return false;
  if (!stepA) return true;
  const sum = a.quantity_delta + b.quantity_delta;
  return sum >= -100 && sum <= 100 && sum !== 0;
};
const inOrder = (a, b) => (a.timestamp - b.timestamp) || (a.id - b.id);

/** How many of this account's changes are waiting (also updates listeners). */
export const refreshPendingCount = async (owner) => {
  if (!owner) { pending = 0; emit(); return 0; }
  try {
    pending = (await getSyncQueue()).filter(mine(owner)).length;
  } catch (e) {
    pending = 0;
  }
  emit();
  return pending;
};

/** Synchronous read of the last known count (for the list page's poller). */
export const pendingWrites = () => pending;

/** Listen for count changes. Returns an unsubscribe function. */
export const onPendingWrites = (fn) => {
  const h = (ev) => fn(ev && ev.detail ? ev.detail.count : pending);
  window.addEventListener(EVENT, h);
  return () => window.removeEventListener(EVENT, h);
};

/**
 * Keep a change to send later. `path` is relative to the API base
 * ("/lists/<id>/items/<item>"), so a change of server address can't strand it.
 *
 * Two changes in a row to the same item (tick, untick, tick) become one: the
 * later fields win, which is exactly what sending both in order would do.
 */
export const queueWrite = async (owner, method, path, body) => {
  if (!owner) throw new Error('offline write needs an account');
  const all = await getSyncQueue();
  const last = all.filter(mine(owner)).sort(inOrder).pop();
  if (last && method === 'put' && last.method === 'put' && last.path === path
      && mergeable(last.body, body)) {
    const merged = { ...(last.body || {}), ...(body || {}) };
    // Two +/- steps are added together, never one replacing the other.
    if (last.body && last.body.quantity_delta != null && body && body.quantity_delta != null) {
      merged.quantity_delta = last.body.quantity_delta + body.quantity_delta;
    }
    await put(STORES.SYNC_QUEUE, { ...last, body: merged });
  } else {
    await addToSyncQueue({ kind: KIND, owner, method, path, body: body || {} });
  }
  await refreshPendingCount(owner);
};

/**
 * Send this account's waiting changes, oldest first. Returns
 * { sent, refused, left }. Only one run at a time; a second call while one is
 * going gets the same run.
 */
export const flushWrites = (owner, api) => {
  if (!owner) return Promise.resolve({ sent: 0, refused: 0, left: 0 });
  if (flushing) return flushing;
  flushing = (async () => {
    let sent = 0;
    let refused = 0;
    if (isOffline()) return { sent, refused, left: await refreshPendingCount(owner) };
    const queue = (await getSyncQueue()).filter(mine(owner)).sort(inOrder);
    for (const a of queue) {
      try {
        await axios({ method: a.method, url: `${api}${a.path}`, data: a.body });
        await removeFromSyncQueue(a.id);
        sent++;
      } catch (e) {
        const s = e && e.response ? e.response.status : 0;
        // No answer, the server struggling, or the sign-in needing renewal:
        // stop here and keep this one and everything after it.
        if (!s || s >= 500 || s === 401 || s === 408 || s === 429) break;
        // The server said no to this change (the list or item is gone).
        await removeFromSyncQueue(a.id);
        refused++;
      }
    }
    const left = await refreshPendingCount(owner);
    if (sent || refused) {
      try { window.dispatchEvent(new CustomEvent(SYNCED_EVENT, { detail: { sent, refused, left } })); } catch (e) { /* noop */ }
    }
    return { sent, refused, left };
  })().finally(() => { flushing = null; });
  return flushing;
};

// ---- The list kept on this phone ----------------------------------------
// The open list is kept here each time it changes, so a list page opened (or
// reloaded) with no signal can still show it. Stored with the account it
// belongs to and only ever shown to that account.

export const keepListOnPhone = async (owner, list) => {
  if (!owner || !list || !list.id) return;
  try { await put(STORES.LISTS, { ...list, _owner: owner }); } catch (e) { /* best effort */ }
};

/** Signing out removes the lists kept on this phone (waiting changes stay,
 *  tagged to their account, and go when that account signs in again). */
export const forgetListsOnPhone = async () => {
  try { await clearStore(STORES.LISTS); } catch (e) { /* best effort */ }
};

export const listKeptOnPhone = async (owner, listId) => {
  if (!owner || !listId) return null;
  try {
    const row = await getById(STORES.LISTS, listId);
    if (!row || row._owner !== owner) return null;
    const { _owner, ...list } = row;
    return list;
  } catch (e) {
    return null;
  }
};
